import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Stack, Telegram24, Typography, useMediaSwitch, useMobile } from 'Modules/ui3';

import { getNewsSlidesArray, getPerView, newsGlider } from './utils';
import { NEWS_DOMAIN_PATH, NewsPaginationElement } from '../../api';
import { NewsSlide, NewsSlideProps } from '../NewsSlide';
import { NewsProps } from './types';
import { Slider } from '../Slider';
import { SliderControls } from '../SliderControls';
import { useSliderPagination } from '../Slider/utils';
import { TELEGRAM_LINK } from '../../constants';


export const News: FC<NewsProps> = React.memo(({ rowCount = 1, ...props }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [slidesViewParams, setSlidesViewParams] = useState({
        count: 3,
        overflow: 0,
    });
    const isMobile = useMobile();

    useEffect(() => {
        setSlidesViewParams(getPerView(containerRef?.current?.offsetWidth, isMobile));
    }, [containerRef, isMobile]);

    const { needShowElement, disableSlider, totalElements } =
        useSliderPagination<NewsPaginationElement>({
            elementsViewCount: slidesViewParams.count * rowCount,
            path: NEWS_DOMAIN_PATH,
        });

    const slides = useMemo(() => getNewsSlidesArray({
        elements: totalElements,
        rowCount,
        colCount: slidesViewParams.count,
    }), [totalElements, rowCount, slidesViewParams.count]);

    return (
        <Stack spacing={useMediaSwitch(16, 12, 6)} {...props}>
            <Stack direction='row' spacing={12} justifyContent='space-between' alignItems='center'>
                <Typography
                    fontFamily='Hauora'
                    fontSize={useMediaSwitch('72px', '56px', '32px')}
                    fontWeight={700}
                    lineHeight={isMobile ? '1.25' : '1.3'}
                >
                    Новости
                </Typography>
                <SliderControls
                    disabled={disableSlider}
                    glider={newsGlider}
                    slidesCount={slides.length}
                    elementsViewCount={slidesViewParams.count}
                />
            </Stack>

            <Slider<NewsSlideProps>
                ref={containerRef}
                glider={newsGlider}
                slideComponent={NewsSlide}
                needShow={needShowElement}
                slidesData={slides}
                elementsViewCount={slidesViewParams.count}
                disabled={disableSlider}
                overflowSizePx={slidesViewParams.overflow}
            />

            <Stack
                component='a'
                href={TELEGRAM_LINK}
                target='_blank'
                rel='noopener noreferrer'
                direction='row'
                alignItems='center'
                spacing={useMediaSwitch(8, 6, 4)}
            >
                <Telegram24 sx={{ fontSize: isMobile ? '48px' : '56px' }} />
                <Typography
                    fontFamily='Hauora'
                    fontSize={isMobile ? '20px' : '24px'}
                    fontWeight={700}
                    lineHeight={isMobile ? '1.6' : '1.3'}
                    color='#242424'
                >
                    Читайте о нас
                </Typography>
            </Stack>
        </Stack>
    );
});
